import React, { useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { SmartLinkWidgetType } from 'fym-common/src/db/enums';
import type { SerializedPresaveSubscribeWidget } from 'fym-shared/src/smartlinks/types';
import { TypedMemo } from '../../../../common/types';
import { SMLFormContainer } from '../../../SMLFormContainer/SMLFormContainer';
import { SMLRoutes } from '../../../../../routing/sml/SMLRoutes';
import { WidgetEditionProps } from '../props';
import { PresaveSubscribeServiceRow } from './PresaveSubscribeServiceRow';
import {
  DEFAULT_PRESAVE_SUBSCRIBE_SERVICES,
  PRESAVE_SUBSCRIBE_SERVICES,
  toggleService,
} from './presaveSubscribeServices';
import { presaveSubscribeMessages as messages } from './presaveSubscribeMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the whole card; each part uses its own slice of it */
import styles from './PresaveSubscribeWidget.module.scss';
/* eslint-enable css-modules/no-unused-class */

/**
 * The "Get every release" block in the Creator (design presave-subscriptions d6): the services fans
 * can pick, saved as `{ services }` with the widget call like every other block. A new block starts
 * with every service. With no service ticked, Done is off and the editor says why. The server
 * refuses the block on Free with a 402, which opens the plan prompt.
 */
const PresaveSubscribeWidgetEditionViewComponent: React.FC<WidgetEditionProps<SerializedPresaveSubscribeWidget>> = ({
  widget,
  accountId,
  smartLinkId,
  saveWidget,
  deleteWidget,
  isLoading,
  error,
}) => {
  const intl = useIntl();
  const [services, setServices] = useState<string[]>(() =>
    PRESAVE_SUBSCRIBE_SERVICES.filter((one) =>
      (widget?.data.services ?? DEFAULT_PRESAVE_SUBSCRIBE_SERVICES).includes(one)
    )
  );
  const backRouteParams = useMemo(() => ({ accountId, smartLinkId }), [accountId, smartLinkId]);
  const onToggle = useCallback((integrationType: string) => {
    setServices((selected) => toggleService(selected, integrationType));
  }, []);
  const submit = useCallback(() => {
    if (services.length === 0) {
      return;
    }
    saveWidget({ services }, SmartLinkWidgetType.presaveSubscribe).catch(console.error);
  }, [saveWidget, services]);

  return (
    <SMLFormContainer
      backRoute={SMLRoutes.details}
      backRouteParams={backRouteParams}
      title={intl.formatMessage(messages.title)}
      onSubmit={submit}
      onDelete={widget ? deleteWidget : undefined}
      submitDisabled={services.length === 0}
      isLoading={isLoading}
      error={error}
    >
      <div className={styles.form} data-testid="presave-subscribe-form">
        <p className={styles.help}>{intl.formatMessage(messages.help)}</p>
        <span className={styles.label}>{intl.formatMessage(messages.servicesLabel)}</span>
        <div className={styles.list}>
          {PRESAVE_SUBSCRIBE_SERVICES.map((integrationType) => (
            <PresaveSubscribeServiceRow
              key={integrationType}
              integrationType={integrationType}
              checked={services.includes(integrationType)}
              disabled={isLoading}
              onToggle={onToggle}
            />
          ))}
        </div>
        {services.length === 0 ? (
          <p className={styles.error} role="alert">
            {intl.formatMessage(messages.noService)}
          </p>
        ) : (
          <p className={styles.help}>{intl.formatMessage(messages.emailNote)}</p>
        )}
      </div>
    </SMLFormContainer>
  );
};

export const PresaveSubscribeWidgetEditionView = TypedMemo(PresaveSubscribeWidgetEditionViewComponent);
